import axios from 'axios';

export const PROXY_FORWARD_URL = '/api/proxy/forward';

/**
 * 解析后端代理 body：支持 JSON 对象、JSON 字符串、JSONP（如 callback123({...})）
 */
export function parseProxyBody(body) {
  if (body == null) {
    return null;
  }
  if (typeof body === 'object') {
    return body;
  }
  if (typeof body !== 'string') {
    return body;
  }

  const trimmed = body.trim();
  const jsonpMatch = trimmed.match(/^[a-zA-Z0-9_$]+\(([\s\S]*)\)\s*;?\s*$/);
  if (jsonpMatch) {
    try {
      return JSON.parse(jsonpMatch[1]);
    } catch {
      // fall through
    }
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return body;
  }
}

/**
 * 解析海棠网播放接口响应
 */
export function extractHaitangwMedia(responseData) {
  const parsed = parseProxyBody(responseData);

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('第三方接口响应格式异常');
  }

  if (parsed.code !== undefined && Number(parsed.code) !== 200) {
    throw new Error(parsed.msg || '获取音频URL失败');
  }

  const audioData = parsed.data ?? parsed;
  if (!audioData?.url) {
    throw new Error('无法获取音频URL');
  }

  return audioData;
}

export function buildMediaSourceResult(audioData, musicItem, quality) {
  const result = {
    url: audioData.url,
    quality,
    duration: musicItem?.duration || 0,
    headers: {}
  };

  const lyricText = audioData.lrc || audioData.lyric;
  if (lyricText) {
    result.lyric = {
      lrc: lyricText,
      tlyric: audioData.tlyric || '',
      rlyric: audioData.rlyric || ''
    };
  }

  return result;
}

async function requestViaBackendProxyRaw(url, headers = {}, options = {}) {
  const proxyRequestPayload = {
    url,
    method: options.method || 'GET',
    headers,
    body: options.body,
    contentType: options.contentType,
    useDisguise: options.useDisguise ?? false
  };

  const proxyResponse = await axios.post(PROXY_FORWARD_URL, proxyRequestPayload);
  const proxyResponseData = proxyResponse?.data;

  if (!proxyResponseData || proxyResponseData.code !== 0) {
    throw new Error(proxyResponseData?.message || '代理请求失败');
  }

  const proxiedResult = proxyResponseData.data;
  if (!proxiedResult?.success) {
    throw new Error(proxiedResult?.message || '第三方接口响应异常');
  }

  return proxiedResult;
}

export async function requestViaBackendProxy(url, headers = {}, options = {}) {
  const proxiedResult = await requestViaBackendProxyRaw(url, headers, options);
  return parseProxyBody(proxiedResult.body);
}

/**
 * 从代理返回的响应头（大小写不敏感）中读取指定 header 的值。
 * 后端 HttpHeaders 经 Jackson 序列化后，值可能是数组形式（MultiValueMap）。
 */
export function findProxyHeader(headersObj, headerName) {
  if (!headersObj || typeof headersObj !== 'object') return null;
  const target = headerName.toLowerCase();
  for (const key of Object.keys(headersObj)) {
    if (key.toLowerCase() === target) {
      const value = headersObj[key];
      return Array.isArray(value) ? value[0] : value;
    }
  }
  return null;
}

/**
 * 解析「长青SVIP音源」等 kgqq1/wy1/musicapi1 系列解析接口。
 * 这类接口有两种典型行为，与常规海棠网 JSON 接口（{code,url,...}）不同：
 * 1. 返回 302 重定向，真实播放地址在 Location 响应头中（如 kg.php / qq.php）；
 * 2. 直接返回音频/视频二进制流（Content-Type: audio/* 或 video/*），此时请求URL本身即为播放地址（如 kw.php）。
 * 因此这里优先检查重定向与二进制响应，命中则直接返回可播放地址，避免误把二进制内容当JSON解析。
 */
export async function resolveRedirectOrBinaryMediaUrl(url, headers = {}, options = {}) {
  const proxiedResult = await requestViaBackendProxyRaw(url, headers, options);

  const statusCode = Number(proxiedResult.statusCode);
  if (statusCode >= 300 && statusCode < 400) {
    const location = findProxyHeader(proxiedResult.headers, 'location');
    if (location) {
      return { url: location };
    }
  }

  const contentType = findProxyHeader(proxiedResult.headers, 'content-type') || '';
  if (/^(audio|video)\//i.test(contentType.trim())) {
    return { url };
  }

  return extractHaitangwMedia(proxiedResult.body);
}

export async function postViaBackendProxy(url, body, headers = {}, options = {}) {
  return requestViaBackendProxy(url, headers, {
    method: 'POST',
    body,
    contentType: options.contentType || 'application/json',
    useDisguise: options.useDisguise ?? false
  });
}
